import React, { useCallback, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import Badge from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { PlusIcon, XIcon } from '../../components/ui/Icons';
import { Label } from '../../components/ui/Label';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import { Textarea } from '../../components/ui/Textarea';
import TextInput from '../../components/ui/TextInput';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import type {
  EmailAiPromptConfiguration,
  EmailAiPromptDefaultAction,
  EmbeddedInConfigurationHub,
} from '../configuration-settings/models';

type DrawerFilter = 'all' | 'compose' | 'read';

const DEFAULT_ACTION_OPTIONS: Array<{ value: EmailAiPromptDefaultAction; label: string }> = [
  { value: 'replace', label: 'Replace' },
  { value: 'insert', label: 'Insert' },
  { value: 'none', label: 'None' },
];

type Draft = {
  code: string;
  name: string;
  text: string;
  defaultAction: EmailAiPromptDefaultAction;
  showInCompose: boolean;
  showInRead: boolean;
  needAdditionalText: boolean;
  needSelectedText: boolean;
  enabled: boolean;
  displayOrder: string;
};

const emptyDraft = (): Draft => ({
  code: '',
  name: '',
  text: '',
  defaultAction: 'replace',
  showInCompose: true,
  showInRead: true,
  needAdditionalText: false,
  needSelectedText: false,
  enabled: true,
  displayOrder: '',
});

function mapApiRow(raw: Record<string, unknown>): EmailAiPromptConfiguration {
  return {
    id: Number(raw.id ?? 0),
    code: String(raw.code ?? ''),
    name: String(raw.name ?? ''),
    text: String(raw.text ?? ''),
    defaultAction: raw.defaultAction as EmailAiPromptDefaultAction,
    showInCompose: raw.showInCompose !== false,
    showInRead: raw.showInRead !== false,
    needAdditionalText: Boolean(raw.needAdditionalText),
    needSelectedText: Boolean(raw.needSelectedText),
    enabled: Boolean(raw.enabled),
    displayOrder: Number(raw.displayOrder ?? 0),
    createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : String(raw.createdAt ?? ''),
    updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : String(raw.updatedAt ?? ''),
  };
}

function draftFromRow(row: EmailAiPromptConfiguration): Draft {
  return {
    code: row.code,
    name: row.name,
    text: row.text,
    defaultAction: row.defaultAction,
    showInCompose: row.showInCompose,
    showInRead: row.showInRead,
    needAdditionalText: row.needAdditionalText,
    needSelectedText: row.needSelectedText,
    enabled: row.enabled,
    displayOrder: String(row.displayOrder),
  };
}

function actionLabel(a: EmailAiPromptDefaultAction): string {
  if (a === 'replace') return 'Replace';
  if (a === 'insert') return 'Insert';
  return 'None';
}

const ComposeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const ReadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
    />
  </svg>
);

const EmailAiPromptsEditor: React.FC<EmbeddedInConfigurationHub> = () => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const [rows, setRows] = useState<EmailAiPromptConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [drawerFilter, setDrawerFilter] = useState<DrawerFilter>('all');

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<unknown[] | Record<string, unknown>>('/settings/email-ai-prompts');
      const list = Array.isArray(data) ? data : [];
      setRows(list.map((item) => mapApiRow(item as Record<string, unknown>)));
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || 'Failed to load email AI prompts';
      showError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  React.useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (a.displayOrder !== b.displayOrder) return a.displayOrder - b.displayOrder;
        return a.name.localeCompare(b.name);
      }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortedRows.filter((row) => {
      if (drawerFilter === 'compose' && !row.showInCompose) return false;
      if (drawerFilter === 'read' && !row.showInRead) return false;
      if (!q) return true;
      return (
        row.name.toLowerCase().includes(q)
        || row.code.toLowerCase().includes(q)
        || row.text.toLowerCase().includes(q)
      );
    });
  }, [sortedRows, search, drawerFilter]);

  const enabledCount = useMemo(() => sortedRows.filter((r) => r.enabled).length, [sortedRows]);

  const thClass = cn('px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide', colors.textMuted);
  const tdClass = cn('px-4 py-3 text-sm align-top', colors.text);
  const rowBorder = cn('border-t', colors.border);

  const resetDraft = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const closePromptModal = () => {
    if (saving) return;
    setPromptModalOpen(false);
    resetDraft();
  };

  const openNewPrompt = () => {
    resetDraft();
    setPromptModalOpen(true);
  };

  const validateDraft = (): string | null => {
    if (!draft.code.trim()) return 'Prompt code (slug) is required.';
    if (!/^[a-z0-9_]+$/i.test(draft.code.trim())) {
      return 'Use letters, numbers, and underscores only for the code.';
    }
    if (!draft.name.trim()) return 'Name is required.';
    if (!draft.text.trim()) return 'Prompt text is required.';
    if (!draft.showInCompose && !draft.showInRead) {
      return 'Enable at least one: Compose or Read.';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validateDraft();
    if (err) {
      showError(err);
      return;
    }
    const displayOrderNum = draft.displayOrder.trim() ? parseInt(draft.displayOrder, 10) : undefined;
    if (draft.displayOrder.trim() && !Number.isFinite(displayOrderNum)) {
      showError('Display order must be a number.');
      return;
    }
    setSaving(true);
    try {
      const base = {
        name: draft.name.trim(),
        text: draft.text,
        defaultAction: draft.defaultAction,
        showInCompose: draft.showInCompose,
        showInRead: draft.showInRead,
        needAdditionalText: draft.needAdditionalText,
        needSelectedText: draft.needSelectedText,
        enabled: draft.enabled,
        ...(displayOrderNum !== undefined ? { displayOrder: displayOrderNum } : {}),
      };
      if (editingId != null) {
        await api.patch(`/settings/email-ai-prompts/${editingId}`, {
          ...base,
          code: draft.code.trim(),
        });
        showSuccess('Prompt updated.');
      } else {
        await api.post('/settings/email-ai-prompts', {
          code: draft.code.trim(),
          ...base,
        });
        showSuccess('Prompt created.');
      }
      await loadRows();
      setPromptModalOpen(false);
      resetDraft();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (e as Error)?.message
        || 'Save failed';
      showError(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: EmailAiPromptConfiguration) => {
    setEditingId(row.id);
    setDraft(draftFromRow(row));
    setPromptModalOpen(true);
  };

  const removeRow = async (id: number) => {
    try {
      await api.delete(`/settings/email-ai-prompts/${id}`);
      showSuccess('Prompt removed.');
      if (editingId === id) {
        setPromptModalOpen(false);
        resetDraft();
      }
      await loadRows();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || 'Delete failed';
      showError(msg);
    }
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (id == null) return;
    setDeleteConfirmId(null);
    await removeRow(id);
  };

  const handleSyncBuiltins = async () => {
    setSyncing(true);
    try {
      await api.post('/settings/email-ai-prompts/sync-builtins');
      showSuccess('Built-in prompts synced (new codes added; existing rows unchanged).');
      await loadRows();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error)?.message
        || 'Sync failed';
      showError(msg);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className={cn('rounded-xl border', colors.border, colors.surface)}>
        <div className="flex flex-col gap-4 border-b px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium tabular-nums',
                'bg-violet-500/10 text-violet-700 dark:text-violet-300',
              )}
            >
              {loading ? '…' : `${sortedRows.length} prompts`}
            </span>
            <span className={cn('text-xs tabular-nums', colors.textMuted)}>
              {loading ? '' : `${enabledCount} enabled`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={loading}
              onClick={() => void loadRows()}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={syncing}
              onClick={() => void handleSyncBuiltins()}
            >
              {syncing ? 'Syncing…' : 'Sync built-ins'}
            </Button>
            <Button type="button" size="sm" className="gap-1.5" onClick={() => openNewPrompt()}>
              <PlusIcon className="h-4 w-4" aria-hidden />
              New prompt
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-4 py-4 sm:px-5 lg:flex-row lg:items-end">
          <div className="min-w-0 flex-1 lg:max-w-md">
            <label htmlFor="ai-prompts-search" className={cn('mb-1.5 block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
              Search
            </label>
            <div className="relative">
              <Search className={cn('pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', colors.textMuted)} aria-hidden />
              <TextInput
                id="ai-prompts-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, code, or template text…"
                className="pl-9"
              />
            </div>
          </div>
          <div>
            <span className={cn('mb-1.5 block text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
              Drawer
            </span>
            <div className="flex flex-wrap gap-2">
              {([
                ['all', 'All'],
                ['compose', 'Compose'],
                ['read', 'Read'],
              ] as const).map(([value, label]) => {
                const active = drawerFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setDrawerFilter(value)}
                    className={cn(
                      'rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                      active
                        ? 'border-violet-500/50 bg-violet-500/15 text-violet-700 dark:text-violet-300'
                        : cn(colors.border, colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5'),
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <details className={cn('border-t px-4 py-3 text-sm sm:px-5', colors.border)}>
          <summary className={cn('cursor-pointer font-medium', colors.textMuted)}>Placeholder reference</summary>
          <p className={cn('mt-2 font-mono text-xs leading-relaxed', colors.textMuted)}>
            {'{%subject%}'}, {'{%body_plain%}'}, {'{%selection_or_body_plain%}'}, {'{%additional_text%}'},{' '}
            {'{%selected_text%}'}
          </p>
        </details>
      </div>

      {loading ? (
        <div className="space-y-3" role="status" aria-label="Loading prompts">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={cn('h-16 animate-pulse rounded-xl', 'bg-slate-200/70 dark:bg-slate-700/50')}
            />
          ))}
        </div>
      ) : filteredRows.length === 0 ? (
        <div
          className={cn(
            'flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-16 text-center',
            colors.border,
            colors.surface,
          )}
        >
          <p className={cn('text-sm font-semibold', colors.text)}>
            {sortedRows.length === 0 ? 'No prompts configured' : 'No prompts match your filters'}
          </p>
          <p className={cn('mt-1 max-w-md text-sm', colors.textMuted)}>
            {sortedRows.length === 0
              ? 'Sync built-in templates or create a custom prompt.'
              : 'Try a different search or drawer filter.'}
          </p>
          {sortedRows.length === 0 ? (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={syncing} onClick={() => void handleSyncBuiltins()}>
                Sync built-ins
              </Button>
              <Button type="button" size="sm" onClick={() => openNewPrompt()}>
                New prompt
              </Button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={cn('overflow-x-auto rounded-xl border', colors.border, colors.surface)}>
          <table className="min-w-full w-full text-left text-sm">
            <thead className={cn(colors.surfaceHover)}>
              <tr className={cn('border-b', colors.border)}>
                <th className={cn(thClass, 'w-14')}>#</th>
                <th className={cn(thClass, 'min-w-[12rem]')}>Prompt</th>
                <th className={cn(thClass, 'min-w-[16rem] hidden lg:table-cell')}>Template preview</th>
                <th className={cn(thClass, 'w-32')}>Drawers</th>
                <th className={cn(thClass, 'w-24')}>Result</th>
                <th className={cn(thClass, 'w-24')}>Status</th>
                <th className={cn(thClass, 'w-24 text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const isEditing = promptModalOpen && editingId === row.id;
                return (
                  <tr
                    key={row.id}
                    className={cn(
                      rowBorder,
                      isEditing && 'bg-violet-500/[0.07] dark:bg-violet-400/[0.09]',
                      !isEditing && 'hover:bg-black/[0.02] dark:hover:bg-white/[0.03]',
                    )}
                  >
                    <td className={tdClass}>
                      <span
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold tabular-nums',
                          'bg-violet-500/12 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300',
                        )}
                      >
                        {row.displayOrder}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <p className="font-semibold leading-snug">{row.name}</p>
                      <code className={cn('mt-0.5 block font-mono text-xs', colors.textMuted)}>{row.code}</code>
                      <p className={cn('mt-1.5 line-clamp-2 font-mono text-[11px] leading-relaxed lg:hidden', colors.textMuted)}>
                        {row.text.replace(/\s+/g, ' ').trim()}
                      </p>
                    </td>
                    <td className={cn(tdClass, 'hidden lg:table-cell max-w-md')}>
                      <p className={cn('line-clamp-3 font-mono text-[11px] leading-relaxed', colors.textMuted)} title={row.text}>
                        {row.text.replace(/\s+/g, ' ').trim()}
                      </p>
                    </td>
                    <td className={tdClass}>
                      <div className="flex flex-col gap-1">
                        {row.showInCompose && (
                          <span className="inline-flex w-fit items-center gap-1 rounded-md bg-sky-500/10 px-2 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-400/15 dark:text-sky-200">
                            <ComposeIcon className="h-3.5 w-3.5 shrink-0" />
                            Compose
                          </span>
                        )}
                        {row.showInRead && (
                          <span className="inline-flex w-fit items-center gap-1 rounded-md bg-violet-500/10 px-2 py-0.5 text-[11px] font-medium text-violet-800 dark:bg-violet-400/15 dark:text-violet-200">
                            <ReadIcon className="h-3.5 w-3.5 shrink-0" />
                            Read
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={tdClass}>
                      <span className={cn('text-xs font-medium', colors.textMuted)}>{actionLabel(row.defaultAction)}</span>
                      {(row.needAdditionalText || row.needSelectedText) && (
                        <div className={cn('mt-1 space-y-0.5 text-[11px]', colors.textMuted)}>
                          {row.needAdditionalText && <p>Extra text</p>}
                          {row.needSelectedText && <p>Selection</p>}
                        </div>
                      )}
                    </td>
                    <td className={tdClass}>
                      <Badge variant={row.enabled ? 'success' : 'secondary'} className="rounded-md">
                        {row.enabled ? 'On' : 'Off'}
                      </Badge>
                    </td>
                    <td className={cn(tdClass, 'text-right')}>
                      <div className="inline-flex items-center justify-end gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className={cn('rounded-lg p-2', colors.textMuted, colors.interactiveHover)}
                          title="Edit"
                          aria-label="Edit prompt"
                          onClick={() => startEdit(row)}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                          title="Delete"
                          aria-label={`Delete ${row.name}`}
                          onClick={() => setDeleteConfirmId(row.id)}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={promptModalOpen}
        onClose={closePromptModal}
        size="xl"
        closeOnEscape={!saving}
      >
        <ModalHeader>
          <div className="flex w-full min-w-0 items-center justify-between gap-3">
            <ModalTitle>{editingId != null ? 'Edit prompt' : 'New prompt'}</ModalTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="shrink-0 rounded-lg p-2"
              aria-label="Close"
              disabled={saving}
              onClick={closePromptModal}
            >
              <XIcon className="h-5 w-5" />
            </Button>
          </div>
        </ModalHeader>
        <form
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
        >
          <ModalContent>
            <p className={cn('text-sm mb-6', colors.textMuted)}>
              Choose where this prompt appears (compose and/or read drawers). Use &quot;Sync built-ins&quot; after upgrades
              to register new shipped codes without overwriting your templates.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label htmlFor="email-ai-prompt-code" required className="mb-1">
                  Code (slug)
                </Label>
                <TextInput
                  id="email-ai-prompt-code"
                  value={draft.code}
                  onChange={(e) => setDraft((p) => ({ ...p, code: e.target.value }))}
                  placeholder="prompt_my_custom"
                  className="mt-1 font-mono text-sm"
                  required
                  autoComplete="off"
                />
                {editingId != null && (
                  <p className={cn('text-xs mt-1', colors.textMuted)}>
                    Changing the code updates the stable key; the record keeps the same internal reference for API
                    updates.
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email-ai-prompt-name" required className="mb-1">
                  Name
                </Label>
                <TextInput
                  id="email-ai-prompt-name"
                  value={draft.name}
                  onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Summarize"
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label className="mb-1">Default action</Label>
                <Select
                  value={draft.defaultAction}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      defaultAction: e.target.value as EmailAiPromptDefaultAction,
                    }))
                  }
                  options={DEFAULT_ACTION_OPTIONS}
                  showClearButton={false}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email-ai-prompt-order" className="mb-1">
                  Display order
                </Label>
                <TextInput
                  id="email-ai-prompt-order"
                  value={draft.displayOrder}
                  onChange={(e) => setDraft((p) => ({ ...p, displayOrder: e.target.value }))}
                  placeholder="0"
                  className="mt-1"
                  inputMode="numeric"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="mb-2 block">Show in drawers</Label>
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch
                      checked={draft.showInCompose}
                      onChange={(e) => setDraft((p) => ({ ...p, showInCompose: e.target.checked }))}
                    />
                    <span className={cn('text-sm', colors.text)}>Compose</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Switch
                      checked={draft.showInRead}
                      onChange={(e) => setDraft((p) => ({ ...p, showInRead: e.target.checked }))}
                    />
                    <span className={cn('text-sm', colors.text)}>Read</span>
                  </label>
                </div>
                <p className={cn('text-xs mt-1.5', colors.textMuted)}>Turn on one or both.</p>
              </div>
              <div className="flex flex-col justify-end gap-4 md:col-span-2 md:flex-row md:items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={draft.enabled}
                    onChange={(e) => setDraft((p) => ({ ...p, enabled: e.target.checked }))}
                  />
                  <span className={cn('text-sm', colors.text)}>Enabled</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={draft.needAdditionalText}
                    onChange={(e) => setDraft((p) => ({ ...p, needAdditionalText: e.target.checked }))}
                  />
                  <span className={cn('text-sm', colors.text)}>Requires additional text</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={draft.needSelectedText}
                    onChange={(e) => setDraft((p) => ({ ...p, needSelectedText: e.target.checked }))}
                  />
                  <span className={cn('text-sm', colors.text)}>Requires selected text</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email-ai-prompt-text" required className="mb-1">
                  Prompt template
                </Label>
                <Textarea
                  id="email-ai-prompt-text"
                  value={draft.text}
                  onChange={(e) => setDraft((p) => ({ ...p, text: e.target.value }))}
                  className={cn('mt-1 min-h-[220px] font-mono text-sm', colors.text)}
                  placeholder="Instructions…\n\n{%body_plain%}"
                  required
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="justify-end gap-2 sm:gap-3">
            <Button type="button" variant="ghost" disabled={saving} onClick={closePromptModal}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={saving}>
              {saving ? 'Saving…' : editingId != null ? 'Save changes' : 'Create prompt'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteConfirmId != null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => void handleDeleteConfirm()}
        title="Delete prompt"
        message="Delete this prompt? The email AI drawer will no longer offer it."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

export default EmailAiPromptsEditor;
